import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IntegrationProviderKey } from 'constants/integrations';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import { openConfirmationDialogAction } from 'store/global/actions';
import {
  closePrepareDisclosuresWizardAction,
  getApplicationBorrowersAction,
  getApplicationConsentsAction,
  getLoanAndLockSummaryEditDataAction,
  getLoanFeesViewAction,
  getPropertyAndLoanSummaryAction,
  getSubjectPropertyAction,
  getUnderwritingDataAction,
} from 'store/loanApplications/actions';
import { getProvidersAction } from 'store/system/actions';
import { selectProviderByKey } from 'store/system/selectors';

import {
  DIRTY_CLOSE_CONFIRMATION,
  INITIAL_BORROWERS_DELIVERY,
  WIZARD_STEPS,
  WizardStep,
} from './PrepareDisclosuresWizard.constants';
import { PrepareDisclosuresWizardContext } from './PrepareDisclosuresWizardContext';

const getStepIndex = step => WIZARD_STEPS.findIndex(({ value }) => value === step);

const PrepareDisclosuresWizardProvider = ({ isOpen, children }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const [activeStep, setActiveStep] = useState(WizardStep.READINESS);
  const [stepGates, setStepGates] = useState({});
  const [stepSubmits, setStepSubmits] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [borrowersDelivery, setBorrowersDelivery] = useState(INITIAL_BORROWERS_DELIVERY);
  const [previewResult, setPreviewResult] = useState(null);
  const [reviewedVersionHash, setReviewedVersionHash] = useState(null);
  const [isSent, setIsSent] = useState(false);
  const [readinessAlertCheckIds, setReadinessAlertCheckIds] = useState([]);

  const lobProvider = useSelector(selectProviderByKey(IntegrationProviderKey.LOB));
  const isMailProviderEnabled = !!lobProvider?.isEnabled;

  useEffectSafeStrictMode(() => {
    if (!isOpen || !applicationId) {
      return;
    }

    dispatch(getApplicationBorrowersAction({ applicationId }));
    dispatch(getApplicationConsentsAction({ applicationId }));
    dispatch(getSubjectPropertyAction({ applicationId }));
    dispatch(getLoanFeesViewAction({ applicationId }));
    dispatch(getLoanAndLockSummaryEditDataAction({ applicationId }));
    dispatch(getPropertyAndLoanSummaryAction({ applicationId }));

    if (loanId) {
      dispatch(getUnderwritingDataAction({ loanId }));
    }

    // A regular LO/processor driving this wizard may not hold `integrations.view` — silence both
    // the error toast and the global forbidden-page-reload so an ungranted claim just falls back to
    // "no provider enabled" instead of interrupting the wizard.
    Promise.resolve(
      dispatch(
        getProvidersAction({ silent: true, skipResponseInterceptor: { forbiddenError: true } }),
      ),
    ).catch(_.noop);
  }, [isOpen, applicationId, loanId, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setActiveStep(WizardStep.READINESS);
      setStepGates({});
      setStepSubmits({});
      setIsDirty(false);
      setBorrowersDelivery(INITIAL_BORROWERS_DELIVERY);
      setPreviewResult(null);
      setReviewedVersionHash(null);
      setIsSent(false);
      setReadinessAlertCheckIds([]);
    }
  }, [isOpen]);

  const registerStepGate = useCallback((step, canContinue) => {
    setStepGates(prev => (prev[step] === canContinue ? prev : { ...prev, [step]: canContinue }));
  }, []);

  const registerStepSubmit = useCallback((step, submit) => {
    setStepSubmits(prev =>
      prev[step] === (submit ?? null) ? prev : { ...prev, [step]: submit ?? null },
    );
  }, []);

  const activeStepIndex = getStepIndex(activeStep);
  const isFirstStep = activeStepIndex <= 0;
  const isLastStep = activeStepIndex === WIZARD_STEPS.length - 1;

  const goToStep = useCallback(step => {
    if (getStepIndex(step) !== -1) {
      setActiveStep(step);
    }
  }, []);

  const goNext = useCallback(() => {
    setActiveStep(prev => {
      const nextStep = WIZARD_STEPS[getStepIndex(prev) + 1];

      return nextStep ? nextStep.value : prev;
    });
  }, []);

  const goBack = useCallback(() => {
    setActiveStep(prev => {
      const prevStep = WIZARD_STEPS[getStepIndex(prev) - 1];

      return prevStep ? prevStep.value : prev;
    });
  }, []);

  const close = useCallback(() => {
    dispatch(closePrepareDisclosuresWizardAction());
  }, [dispatch]);

  const requestClose = useCallback(() => {
    if (!isDirty) {
      close();

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: DIRTY_CLOSE_CONFIRMATION.message,
        description: DIRTY_CLOSE_CONFIRMATION.description,
        onAcceptButtonName: DIRTY_CLOSE_CONFIRMATION.onAcceptButtonName,
        onAccept: close,
      }),
    );
  }, [dispatch, isDirty, close]);

  const submit = useCallback(() => {
    const stepSubmit = stepSubmits[activeStep];
    const canSubmitStep = (stepGates[activeStep] ?? true) && !!stepSubmit;

    if (canSubmitStep) {
      stepSubmit();
    }
  }, [stepSubmits, stepGates, activeStep]);

  const canContinue = stepGates[activeStep] ?? true;
  const canSubmit = !!stepSubmits[activeStep] && canContinue;

  const value = useMemo(
    () => ({
      isOpen,
      steps: WIZARD_STEPS,
      activeStep,
      activeStepIndex,
      isFirstStep,
      isLastStep,
      canContinue,
      canSubmit,
      isDirty,
      loanId,
      applicationId,
      borrowersDelivery,
      setBorrowersDelivery,
      previewResult,
      setPreviewResult,
      reviewedVersionHash,
      setReviewedVersionHash,
      isSent,
      setSent: setIsSent,
      readinessAlertCheckIds,
      setReadinessAlert: setReadinessAlertCheckIds,
      setDirty: setIsDirty,
      isMailProviderEnabled,
      registerStepGate,
      registerStepSubmit,
      goToStep,
      goNext,
      goBack,
      submit,
      requestClose,
    }),
    [
      isOpen,
      activeStep,
      activeStepIndex,
      isFirstStep,
      isLastStep,
      canContinue,
      canSubmit,
      isDirty,
      loanId,
      applicationId,
      borrowersDelivery,
      setBorrowersDelivery,
      previewResult,
      reviewedVersionHash,
      isSent,
      readinessAlertCheckIds,
      isMailProviderEnabled,
      registerStepGate,
      registerStepSubmit,
      goToStep,
      goNext,
      goBack,
      submit,
      requestClose,
    ],
  );

  return (
    <PrepareDisclosuresWizardContext.Provider value={value}>
      {children}
    </PrepareDisclosuresWizardContext.Provider>
  );
};

PrepareDisclosuresWizardProvider.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  children: PropTypes.node,
};

export default PrepareDisclosuresWizardProvider;
